import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ReactECharts from 'echarts-for-react'
import { Loader2, RefreshCw, Users, Clock, BookOpen, Navigation, Search, Layout, Calendar, X, ArrowDown, FileDown, Image } from 'lucide-react'
import { metricsApiClient } from '../../api/http/wireApiClient'
import { httpArticleService } from '../../api/http/articleService'
import { httpPageService } from '../../api/http/pageService'
import type { WireArticle } from '../../api/types/articles'
import type { Page } from '../../api/types/pages'
import type {
  ArticleActivityEntry,
  ArticleNavMethodEntry,
  ArticleNavMethodsByArticleEntry,
  ArticleVisualActivityEntry,
} from './types'
import { dateToApi, todayStr, nDaysAgoStr } from './helpers'
import { useReadershipPdf } from '../../pdf/useReadershipPdf'
import type { ReadershipsReportData } from '../../pdf/useReadershipPdf'

// ── Helpers ───────────────────────────────────────────────────────────────────

function parseCn(dn: string): string {
  const match = /^CN=([^,]+)/i.exec(dn.trim())
  if (!match) return dn
  return match[1].replace(/\b\w/g, c => c.toUpperCase())
}

function fmtDuration(ms: number): string {
  const s = Math.round(ms / 1000)
  if (s < 60) return `${s}s`
  if (s < 3600) return `${Math.round(s / 60)}m`
  const h = Math.floor(s / 3600)
  const m = Math.round((s % 3600) / 60)
  return m > 0 ? `${h}h ${m}m` : `${h}h`
}

function fmtNavMethod(method: string): string {
  if (!method || method.toUpperCase() === 'UNKNOWN') return 'Direct Navigation'
  return method
    .replace(/-/g, ' ')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
}

function truncate(s: string, n: number) {
  return s.length > n ? s.slice(0, n - 1) + '…' : s
}

function captureChart(id: string): string | null {
  const container = document.querySelector(`[data-chart="${id}"]`)
  if (!container) return null
  const sourceCanvas = container.querySelector('canvas')
  if (!sourceCanvas) return null
  const offscreen = document.createElement('canvas')
  offscreen.width  = sourceCanvas.width
  offscreen.height = sourceCanvas.height
  const ctx = offscreen.getContext('2d')
  if (!ctx) return null
  ctx.fillStyle = '#fcfcfc'
  ctx.fillRect(0, 0, offscreen.width, offscreen.height)
  ctx.drawImage(sourceCanvas, 0, 0)
  return offscreen.toDataURL('image/png')
}

function isoToDateStr(iso: string): string {
  return iso.slice(0, 10)
}

function extractArticleIdsFromPage(page: Page): string[] {
  const ids = new Set<string>()
  for (const block of page.blocks) {
    block.content?.articleIds?.forEach(id => ids.add(id))
    if (block.columnContent) {
      for (const col of Object.values(block.columnContent)) {
        col?.forEach(cb => cb.content?.articleIds?.forEach(id => ids.add(id)))
      }
    }
  }
  return Array.from(ids)
}

// ── Aggregated article row ────────────────────────────────────────────────────

type ArticleRollup = {
  id: string
  totalTimeMs: number
  uniqueReaders: number
  avgScrollDepth: number
  pingCount: number
}

function rollupActivity(rows: ArticleActivityEntry[]): ArticleRollup[] {
  const map = new Map<string, { totalTimeMs: number; readers: Set<string>; scrollSum: number; pingCount: number }>()
  for (const row of rows) {
    const existing = map.get(row.articleId)
    if (existing) {
      existing.totalTimeMs += row.timeSpentMs
      existing.readers.add(row.clientDn)
      existing.scrollSum += row.maxScrollYIndex
      existing.pingCount += row.pingCount
    } else {
      map.set(row.articleId, {
        totalTimeMs: row.timeSpentMs,
        readers: new Set([row.clientDn]),
        scrollSum: row.maxScrollYIndex,
        pingCount: row.pingCount,
      })
    }
  }
  return Array.from(map.entries())
    .map(([id, v]) => ({
      id,
      totalTimeMs: v.totalTimeMs,
      uniqueReaders: v.readers.size,
      avgScrollDepth: v.readers.size > 0 ? Math.round(v.scrollSum / v.readers.size) : 0,
      pingCount: v.pingCount,
    }))
    .sort((a, b) => b.totalTimeMs - a.totalTimeMs)
}

type ClientRollup = { cn: string; dn: string; totalTimeMs: number; articleCount: number; pingCount: number; maxScrollDepth: number }

function buildClientRollups(rows: ArticleActivityEntry[]): ClientRollup[] {
  const map = new Map<string, ClientRollup>()
  for (const row of rows) {
    const existing = map.get(row.clientDn)
    if (existing) {
      existing.totalTimeMs += row.timeSpentMs
      existing.articleCount += 1
      existing.pingCount += row.pingCount
      existing.maxScrollDepth = Math.max(existing.maxScrollDepth, row.maxScrollYIndex)
    } else {
      map.set(row.clientDn, {
        cn: parseCn(row.clientDn),
        dn: row.clientDn,
        totalTimeMs: row.timeSpentMs,
        articleCount: 1,
        pingCount: row.pingCount,
        maxScrollDepth: row.maxScrollYIndex,
      })
    }
  }
  return Array.from(map.values()).sort((a, b) => b.totalTimeMs - a.totalTimeMs)
}

type VisualRollup = {
  articleId: string
  visualId: string
  interactions: number
  uniqueReaders: number
  topMethod: string
  visualType: string
  methodCounts: Record<string, number>
  methodReaderCounts: Record<string, number>
}

type VisualMetricOption = {
  id: string
  label: string
  method?: string
}

const COMMON_VISUAL_METRICS: VisualMetricOption[] = [
  { id: 'readersAny', label: 'Readers Who Engaged' },
  { id: 'inView', label: 'Scrolled Into View', method: 'VIEW_ENTER' },
]

const VISUAL_TYPE_METRICS: Record<string, VisualMetricOption[]> = {
  IMAGE: [
    ...COMMON_VISUAL_METRICS,
    { id: 'clicks', label: 'Clicks', method: 'CLICK' },
    { id: 'hovers', label: 'Hovers', method: 'HOVER_OVER' },
    { id: 'lightboxOpens', label: 'Lightbox Opens', method: 'LIGHTBOX_OPEN' },
    { id: 'lightboxCloses', label: 'Lightbox Closes', method: 'LIGHTBOX_CLOSE' },
  ],
  GALLERY: [
    ...COMMON_VISUAL_METRICS,
    { id: 'clicks', label: 'Clicks', method: 'CLICK' },
    { id: 'hovers', label: 'Hovers', method: 'HOVER_OVER' },
    { id: 'drags', label: 'Drags', method: 'DRAG' },
  ],
  LEGACY_VISUAL: [
    ...COMMON_VISUAL_METRICS,
    { id: 'clicks', label: 'Clicks', method: 'CLICK' },
    { id: 'hovers', label: 'Hovers', method: 'HOVER_OVER' },
    { id: 'lightboxOpens', label: 'Lightbox Opens', method: 'LIGHTBOX_OPEN' },
    { id: 'lightboxCloses', label: 'Lightbox Closes', method: 'LIGHTBOX_CLOSE' },
  ],
  VIDEO: [
    ...COMMON_VISUAL_METRICS,
    { id: 'plays', label: 'Plays', method: 'PLAY' },
    { id: 'fullscreenOpens', label: 'Fullscreen Opens', method: 'FULLSCREEN_OPEN' },
    { id: 'pauses', label: 'Pauses', method: 'PAUSE' },
    { id: 'seeks', label: 'Seeks', method: 'SEEK' },
    { id: 'completes', label: 'Completes', method: 'COMPLETE' },
    { id: 'hovers', label: 'Hovers', method: 'HOVER_OVER' },
  ],
  TABLE: [
    ...COMMON_VISUAL_METRICS,
    { id: 'hovers', label: 'Hovers', method: 'HOVER_OVER' },
    { id: 'clicks', label: 'Clicks', method: 'CLICK' },
  ],
  COMPARISON: [
    ...COMMON_VISUAL_METRICS,
    { id: 'hovers', label: 'Hovers', method: 'HOVER_OVER' },
    { id: 'drags', label: 'Drags', method: 'DRAG' },
  ],
}

function rollupVisualActivity(rows: ArticleVisualActivityEntry[]): VisualRollup[] {
  const map = new Map<string, {
    articleId: string
    visualId: string
    interactions: number
    readers: Set<string>
    methods: Map<string, number>
    methodReaders: Map<string, Set<string>>
    visualType: string
  }>()

  for (const row of rows) {
    const key = `${row.articleId}::${row.visualId}`
    const existing = map.get(key)
    if (existing) {
      existing.interactions += row.pingCount
      existing.readers.add(row.clientDn)
      existing.methods.set(row.interactionMethod, (existing.methods.get(row.interactionMethod) ?? 0) + row.pingCount)
      const mr = existing.methodReaders.get(row.interactionMethod)
      if (mr) {
        mr.add(row.clientDn)
      } else {
        existing.methodReaders.set(row.interactionMethod, new Set([row.clientDn]))
      }
    } else {
      map.set(key, {
        articleId: row.articleId,
        visualId: row.visualId,
        interactions: row.pingCount,
        readers: new Set([row.clientDn]),
        methods: new Map([[row.interactionMethod, row.pingCount]]),
        methodReaders: new Map([[row.interactionMethod, new Set([row.clientDn])]]),
        visualType: row.visualType,
      })
    }
  }

  return Array.from(map.values())
    .map(v => {
      const topMethod = Array.from(v.methodReaders.entries()).sort((a, b) => b[1].size - a[1].size)[0]?.[0] ?? 'UNKNOWN'
      return {
        articleId: v.articleId,
        visualId: v.visualId,
        interactions: v.interactions,
        uniqueReaders: v.readers.size,
        topMethod,
        visualType: v.visualType,
        methodReaderCounts: Object.fromEntries(
          Array.from(v.methodReaders.entries()).map(([method, readers]) => [method, readers.size]),
        ),
        methodCounts: Object.fromEntries(v.methods.entries()),
      }
    })
    .sort((a, b) => b.uniqueReaders - a.uniqueReaders)
}

type VisualStoryTrack = {
  primary: VisualMetricOption[]
  orthogonal: VisualMetricOption[]
}

function getVisualStoryTrack(visualType: string): VisualStoryTrack {
  if (visualType === 'VIDEO') {
    return {
      primary: [
        { id: 'inView', label: 'Saw the Video', method: 'VIEW_ENTER' },
        { id: 'plays', label: 'Played It', method: 'PLAY' },
        { id: 'completes', label: 'Finished It', method: 'COMPLETE' },
      ],
      orthogonal: [
        { id: 'fullscreenOpens', label: 'Opened Fullscreen', method: 'FULLSCREEN_OPEN' },
      ],
    }
  }

  if (visualType === 'AUDIO') {
    return {
      primary: [
        { id: 'inView', label: 'Saw the Audio Block', method: 'VIEW_ENTER' },
        { id: 'plays', label: 'Played It', method: 'PLAY' },
        { id: 'completes', label: 'Finished It', method: 'COMPLETE' },
      ],
      orthogonal: [
        { id: 'seeks', label: 'Seeked', method: 'SEEK' },
      ],
    }
  }

  if (visualType === 'IMAGE' || visualType === 'LEGACY_VISUAL') {
    return {
      primary: [
        { id: 'inView', label: 'Saw the Image', method: 'VIEW_ENTER' },
        { id: 'clicks', label: 'Clicked It', method: 'CLICK' },
        { id: 'lightboxOpens', label: 'Opened the Viewer', method: 'LIGHTBOX_OPEN' },
      ],
      orthogonal: [
        { id: 'hovers', label: 'Hovered', method: 'HOVER_OVER' },
      ],
    }
  }

  if (visualType === 'GALLERY' || visualType === 'COMPARISON') {
    if (visualType === 'COMPARISON') {
      return {
        primary: [
          { id: 'inView', label: 'Saw the Gallery', method: 'VIEW_ENTER' },
          { id: 'drags', label: 'Used the Comparison Slider', method: 'DRAG' },
        ],
        orthogonal: [],
      }
    }

    return {
      primary: [
        { id: 'inView', label: 'Saw the Gallery', method: 'VIEW_ENTER' },
        { id: 'drags', label: 'Moved Through It', method: 'DRAG' },
        { id: 'clicks', label: 'Clicked It', method: 'CLICK' },
      ],
      orthogonal: [
        { id: 'hovers', label: 'Hovered', method: 'HOVER_OVER' },
      ],
    }
  }

  return {
    primary: [
      { id: 'inView', label: 'Saw the Visual', method: 'VIEW_ENTER' },
      { id: 'hovers', label: 'Hovered', method: 'HOVER_OVER' },
      { id: 'clicks', label: 'Clicked It', method: 'CLICK' },
    ],
    orthogonal: [],
  }
}

function getVisualStepReaders(visual: VisualRollup, step: VisualMetricOption): number {
  if (!step.method) return visual.uniqueReaders
  return visual.methodReaderCounts[step.method] ?? 0
}

function VisualStoryChart({
  visual,
  totalReaders,
  primarySteps,
  colors,
}: {
  visual: VisualRollup
  totalReaders: number
  primarySteps: VisualMetricOption[]
  colors: ChartColors
}) {
  const chartData = primarySteps.map(step => ({
    name: step.label,
    value: getVisualStepReaders(visual, step),
  }))
  const maxValue = Math.max(totalReaders, ...chartData.map(step => step.value), 1)

  const option = {
    backgroundColor: 'transparent',
    textStyle: { color: colors.fg },
    tooltip: {
      trigger: 'item' as const,
      backgroundColor: colors.tooltipBg,
      borderColor: colors.line,
      textStyle: { color: colors.tooltipFg, fontSize: 11 },
      formatter: (params: { dataIndex: number }) => {
        const current = chartData[params.dataIndex]
        const previous = params.dataIndex > 0 ? chartData[params.dataIndex - 1] : null
        const fromPrevious = previous && previous.value > 0 ? Math.round((current.value / previous.value) * 1000) / 10 : null
        return [
          `<b>${current.name}</b>`,
          `Readers: <b>${current.value.toLocaleString()}</b>`,
          previous ? `From prior step: <b>${fromPrevious?.toFixed(1) ?? '0.0'}%</b>` : `Of article readers: <b>${totalReaders > 0 ? Math.round((current.value / totalReaders) * 1000) / 10 : 0}%</b>`,
        ].join('<br/>')
      },
    },
    series: [{
      type: 'funnel' as const,
      left: '2%',
      top: 8,
      bottom: 8,
      width: '62%',
      min: 0,
      max: maxValue,
      sort: 'none' as const,
      gap: 4,
      label: {
        show: true,
        position: 'right' as const,
        color: colors.fg,
        formatter: (params: { dataIndex: number }) => {
          const current = chartData[params.dataIndex]
          const pctTotal = totalReaders > 0 ? Math.round((current.value / totalReaders) * 1000) / 10 : 0
          return `${current.name}\n${current.value.toLocaleString()} readers · ${pctTotal.toFixed(1)}% of total`
        },
      },
      labelLine: {
        lineStyle: { color: colors.line },
      },
      itemStyle: {
        borderColor: colors.line,
        borderWidth: 1,
      },
      emphasis: {
        label: { color: colors.fg },
      },
      data: chartData,
    }],
  }

  return <ReactECharts option={option} style={{ height: 260 }} />
}

function VisualsPanel({
  totalReaders,
  rows,
}: {
  totalReaders: number
  rows: ArticleVisualActivityEntry[]
}) {
  const colors = useChartTheme()
  const rollups = rollupVisualActivity(rows)

  const [selectedVisualId, setSelectedVisualId] = useState<string>('')

  useEffect(() => {
    if (rollups.length === 0) {
      setSelectedVisualId('')
      return
    }
    if (!rollups.some(r => r.visualId === selectedVisualId)) {
      setSelectedVisualId(rollups[0].visualId)
    }
  }, [rollups, selectedVisualId])

  const selectedVisual = rollups.find(r => r.visualId === selectedVisualId) ?? rollups[0] ?? null

  const visualType = selectedVisual?.visualType?.toUpperCase() ?? 'ALL'
  const metricOptions = VISUAL_TYPE_METRICS[visualType] ?? COMMON_VISUAL_METRICS
  const [selectedMetricId, setSelectedMetricId] = useState<string>(metricOptions[0]?.id ?? 'readersAny')

  useEffect(() => {
    if (!metricOptions.some(m => m.id === selectedMetricId)) {
      setSelectedMetricId(metricOptions[0]?.id ?? 'readersAny')
    }
  }, [metricOptions, selectedMetricId])

  const selectedMetric = metricOptions.find(m => m.id === selectedMetricId) ?? metricOptions[0]
  const storyTrack = getVisualStoryTrack(visualType)

  const totalReadersSafe = Math.max(totalReaders, 0)
  const metricReaders = (() => {
    if (!selectedVisual || !selectedMetric) return 0
    if (!selectedMetric.method) return selectedVisual.uniqueReaders
    return selectedVisual.methodReaderCounts[selectedMetric.method] ?? 0
  })()
  const metricPctTotal = totalReadersSafe > 0 ? Math.round((metricReaders / totalReadersSafe) * 1000) / 10 : 0

  if (rows.length === 0 || !selectedVisual) {
    return <p className="py-12 text-center text-sm text-fg-secondary opacity-60">No visual activity found for this article in the selected date range.</p>
  }

  const primaryStepReaders = storyTrack.primary.map(step => getVisualStepReaders(selectedVisual, step))
  const secondaryStepReaders = storyTrack.orthogonal.map(step => getVisualStepReaders(selectedVisual, step))
  const inViewReaders = primaryStepReaders[0] ?? 0
  const nextStepReaders = primaryStepReaders[1] ?? 0
  const finalStepReaders = primaryStepReaders[2] ?? 0
  const fullscreenReaders = secondaryStepReaders[0] ?? 0
  const nextStepFromViewPct = inViewReaders > 0 ? Math.round((nextStepReaders / inViewReaders) * 1000) / 10 : 0
  const finalFromNextPct = nextStepReaders > 0 ? Math.round((finalStepReaders / nextStepReaders) * 1000) / 10 : 0
  const fullscreenFromNextPct = nextStepReaders > 0 ? Math.round((fullscreenReaders / nextStepReaders) * 1000) / 10 : 0
  const hasFinalPrimaryStep = storyTrack.primary.length > 2
  const firstTransitionLabel = `Saw the Visual → ${storyTrack.primary[1]?.label ?? 'Next Step'}`
  const secondTransitionLabel = hasFinalPrimaryStep
    ? `${storyTrack.primary[1]?.label ?? 'Next Step'} → ${storyTrack.primary[2]?.label ?? 'Final Step'}`
    : ''
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-line bg-surface px-4 py-3">
        <label className="flex items-center gap-2 text-[11px] text-fg-secondary">
          <span className="font-semibold">Visual</span>
          <select
            value={selectedVisual.visualId}
            onChange={(e) => setSelectedVisualId(e.target.value)}
            className="rounded border border-line bg-canvas px-2 py-1 text-xs text-fg focus:border-accent-primary-solid focus:outline-none"
          >
            {rollups.map(v => (
              <option key={v.visualId} value={v.visualId}>
                {truncate(v.visualId, 16)} · {v.visualType}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-[11px] text-fg-secondary">
          <span className="font-semibold">Metric</span>
          <select
            value={selectedMetricId}
            onChange={(e) => setSelectedMetricId(e.target.value)}
            className="rounded border border-line bg-canvas px-2 py-1 text-xs text-fg focus:border-accent-primary-solid focus:outline-none"
          >
            {metricOptions.map(m => (
              <option key={m.id} value={m.id}>{m.label}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        <StatCard icon={Users} label="Total Article Readers" value={totalReadersSafe.toLocaleString()} />
        <StatCard icon={Navigation} label={selectedMetric.label} value={metricReaders.toLocaleString()} />
        <StatCard icon={BookOpen} label="% Of Article Readers" value={`${metricPctTotal.toFixed(1)}%`} />
        <StatCard
          icon={Image}
          label="Visual Type"
          value={selectedVisual.visualType}
          sub={`Top reader action: ${fmtNavMethod(selectedVisual.topMethod)}`}
        />
      </div>

      <div>
        <SectionHeader label={`${selectedVisual.visualType} Conversion Story`} />
        <p className="mb-3 text-[11px] text-fg-secondary">
          Start with the people who saw the visual, then follow the conversion path to the action that matters most.
          Fullscreen is shown separately because it is useful context, not the main burndown.
        </p>
        <div className="rounded-lg border border-line bg-surface p-4">
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,0.9fr)] lg:items-start">
            <VisualStoryChart
              visual={selectedVisual}
              totalReaders={totalReadersSafe}
              primarySteps={storyTrack.primary}
              colors={colors}
            />
            <div className="rounded-lg border border-line bg-canvas p-3">
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-fg-secondary">Visual Metrics</p>
              <div className="divide-y divide-line">
                <div className="flex items-start justify-between gap-3 py-2">
                  <div>
                    <p className="text-[11px] text-fg">Saw the Visual</p>
                    <p className="text-[10px] text-fg-secondary">
                      {totalReadersSafe > 0 ? Math.round((inViewReaders / totalReadersSafe) * 1000) / 10 : 0}% of article readers
                    </p>
                  </div>
                  <p className="text-sm font-semibold tabular-nums text-fg">{inViewReaders.toLocaleString()}</p>
                </div>
                <div className="flex items-start justify-between gap-3 py-2">
                  <div>
                    <p className="text-[11px] text-fg">{firstTransitionLabel}</p>
                    <p className="text-[10px] text-fg-secondary">{nextStepReaders.toLocaleString()} of {inViewReaders.toLocaleString()} continued</p>
                  </div>
                  <p className="text-sm font-semibold tabular-nums text-fg">{nextStepFromViewPct.toFixed(1)}%</p>
                </div>
                {hasFinalPrimaryStep && (
                  <div className="flex items-start justify-between gap-3 py-2">
                    <div>
                      <p className="text-[11px] text-fg">{secondTransitionLabel}</p>
                      <p className="text-[10px] text-fg-secondary">{finalStepReaders.toLocaleString()} of {nextStepReaders.toLocaleString()} reached end</p>
                    </div>
                    <p className="text-sm font-semibold tabular-nums text-fg">{finalFromNextPct.toFixed(1)}%</p>
                  </div>
                )}
                {storyTrack.orthogonal.length > 0 && (
                  <div className="flex items-start justify-between gap-3 py-2">
                    <div>
                      <p className="text-[11px] text-fg">{storyTrack.orthogonal[0].label}</p>
                      <p className="text-[10px] text-fg-secondary">Share of readers who continued</p>
                    </div>
                    <p className="text-sm font-semibold tabular-nums text-fg">{fullscreenFromNextPct.toFixed(1)}%</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div>
        <SectionHeader label="Method Detail" />
        <div className="overflow-hidden rounded border border-line">
          <table className="w-full text-[11px]">
            <thead className="bg-fill">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-fg-secondary">Method</th>
                <th className="px-3 py-2 text-right font-semibold text-fg-secondary">Readers</th>
                <th className="px-3 py-2 text-right font-semibold text-fg-secondary">% Of Article Readers</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {Object.entries(selectedVisual.methodReaderCounts)
                .sort((a, b) => b[1] - a[1])
                .map(([method]) => {
                  const readers = selectedVisual.methodReaderCounts[method] ?? 0
                  const pctTotal = totalReadersSafe > 0 ? (readers / totalReadersSafe) * 100 : 0
                  return (
                    <tr key={method} className="hover:bg-fill/50">
                      <td className="px-3 py-2 text-fg">{fmtNavMethod(method)}</td>
                      <td className="px-3 py-2 text-right text-fg-secondary">{readers.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right text-fg-secondary">{pctTotal.toFixed(1)}%</td>
                    </tr>
                  )
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// ── Chart theme ───────────────────────────────────────────────────────────────

type ChartColors = {
  fg: string
  fgSecondary: string
  line: string
  tooltipBg: string
  tooltipFg: string
  accent: string
}

function resolveChartColors(): ChartColors {
  const s = getComputedStyle(document.documentElement)
  const get = (v: string) => s.getPropertyValue(v).trim()
  return {
    fg:          get('--gray-12') || '#111111',
    fgSecondary: get('--gray-11') || '#6b7280',
    line:        get('--gray-6')  || '#e5e7eb',
    tooltipBg:   get('--gray-12') || '#111111',
    tooltipFg:   get('--gray-1')  || '#fcfcfc',
    accent:      get('--violet-9') || '#7c3aed',
  }
}

function useChartTheme(): ChartColors {
  const [colors, setColors] = useState<ChartColors>(resolveChartColors)
  useEffect(() => {
    const obs = new MutationObserver(() => setColors(resolveChartColors()))
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    return () => obs.disconnect()
  }, [])
  return colors
}

// ── Stat card ─────────────────────────────────────────────────────────────────

function StatCard({
  icon: Icon,
  label,
  value,
  sub,
}: {
  icon: React.ElementType
  label: string
  value: string
  sub?: string
}) {
  return (
    <div className="rounded-lg border border-line bg-surface px-4 py-3">
      <div className="mb-2 flex items-center gap-1.5 text-fg-secondary">
        <Icon size={11} aria-hidden="true" />
        <p className="text-[9px] font-bold uppercase tracking-widest">{label}</p>
      </div>
      <p className="text-xl font-bold text-fg">{value}</p>
      {sub && <p className="mt-0.5 text-[11px] text-fg-secondary">{sub}</p>}
    </div>
  )
}

// ── Top articles chart ────────────────────────────────────────────────────────

function TopArticlesChart({
  rollups,
  colors,
  titles,
  chartId,
}: {
  rollups: ArticleRollup[]
  colors: ChartColors
  titles: Map<string, string>
  chartId?: string
}) {
  const top = rollups.slice(0, 15)
  if (top.length === 0) return <p className="py-8 text-center text-sm text-fg-secondary opacity-60">No data</p>

  const label = (id: string) => truncate(titles.get(id) ?? id, 40)

  const option = {
    backgroundColor: 'transparent',
    textStyle: { color: colors.fg },
    tooltip: {
      trigger: 'axis' as const,
      axisPointer: { type: 'shadow' as const },
      backgroundColor: colors.tooltipBg,
      borderColor: colors.line,
      textStyle: { color: colors.tooltipFg, fontSize: 11 },
      formatter: (params: { dataIndex: number }[]) => {
        const r = top[params[0].dataIndex]
        const title = titles.get(r.id)
        return [
          title ? `<b>${truncate(title, 60)}</b>` : `<b>${r.id}</b>`,
          title ? `<span style="font-size:10px;opacity:0.6">${r.id}</span>` : null,
          `Time spent: <b>${fmtDuration(r.totalTimeMs)}</b>`,
          `Unique readers: <b>${r.uniqueReaders}</b>`,
          `Avg scroll depth: <b>${r.avgScrollDepth}</b>`,
        ].filter(Boolean).join('<br/>')
      },
    },
    grid: { top: 8, right: 100, bottom: 8, left: 8, containLabel: true },
    xAxis: {
      type: 'value' as const,
      axisLabel: {
        color: colors.fgSecondary,
        fontSize: 10,
        formatter: (v: number) => fmtDuration(v),
      },
      splitLine: { lineStyle: { color: colors.line } },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'category' as const,
      data: top.map(r => label(r.id)),
      inverse: true,
      axisLabel: {
        color: colors.fgSecondary,
        fontSize: 10,
        fontFamily: 'monospace',
      },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    series: [{
      type: 'bar' as const,
      data: top.map(r => r.totalTimeMs),
      itemStyle: { color: colors.accent, borderRadius: [0, 3, 3, 0] },
      label: {
        show: true,
        position: 'right' as const,
        formatter: (p: { value: number }) => fmtDuration(p.value),
        color: colors.fgSecondary,
        fontSize: 10,
      },
    }],
  }

  return (
    <div {...(chartId ? { 'data-chart': chartId } : {})}>
      <ReactECharts option={option} style={{ height: Math.max(200, top.length * 28) }} />
    </div>
  )
}

// ── Nav method chart ──────────────────────────────────────────────────────────

const NAV_METHOD_COLORS = [
  '#7c3aed', '#2563eb', '#0891b2', '#059669', '#d97706',
  '#dc2626', '#db2777', '#7c3aed', '#6366f1', '#84cc16',
]

function NavMethodChart({ entries, colors, chartId }: { entries: ArticleNavMethodEntry[]; colors: ChartColors; chartId?: string }) {
  const sorted = [...entries].sort((a, b) => b.uniqueClientsCount - a.uniqueClientsCount)

  if (sorted.length === 0) return <p className="py-8 text-center text-sm text-fg-secondary opacity-60">No data</p>

  const pieData = sorted.map((e, i) => ({
    name: fmtNavMethod(e.navMethod),
    value: e.uniqueClientsCount,
    itemStyle: { color: NAV_METHOD_COLORS[i % NAV_METHOD_COLORS.length] },
  }))

  const option = {
    backgroundColor: 'transparent',
    textStyle: { color: colors.fg },
    tooltip: {
      trigger: 'item' as const,
      backgroundColor: colors.tooltipBg,
      borderColor: colors.line,
      textStyle: { color: colors.tooltipFg, fontSize: 11 },
      formatter: (p: { name: string; value: number; percent: number }) =>
        `${p.name}: <b>${p.value.toLocaleString()}</b> (${p.percent}%)`,
    },
    legend: {
      orient: 'vertical' as const,
      right: 0,
      top: 'middle',
      itemWidth: 10,
      itemHeight: 10,
      textStyle: { color: colors.fgSecondary, fontSize: 10 },
    },
    series: [{
      type: 'pie' as const,
      radius: ['40%', '70%'],
      center: ['35%', '50%'],
      data: pieData,
      label: { show: false },
      emphasis: { label: { show: false } },
    }],
  }

  return (
    <div {...(chartId ? { 'data-chart': chartId } : {})}>
      <ReactECharts option={option} style={{ height: 220 }} />
    </div>
  )
}

// ── Per-client table ──────────────────────────────────────────────────────────

function ClientTable({ rows }: { rows: ClientRollup[] }) {
  const top = rows.slice(0, 20)
  return (
    <div className="overflow-hidden rounded border border-line">
      <table className="w-full text-[11px]">
        <thead className="bg-fill">
          <tr>
            <th className="px-3 py-2 text-left font-semibold text-fg-secondary">#</th>
            <th className="px-3 py-2 text-left font-semibold text-fg-secondary">Reader</th>
            <th className="px-3 py-2 text-right font-semibold text-fg-secondary">Time Spent</th>
            <th className="px-3 py-2 text-right font-semibold text-fg-secondary">Articles</th>
            <th className="px-3 py-2 text-right font-semibold text-fg-secondary">Pings</th>
            <th className="px-3 py-2 text-right font-semibold text-fg-secondary">Max Scroll</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {top.map((r, i) => (
            <tr key={r.dn} className="hover:bg-fill/50">
              <td className="px-3 py-2 text-fg-secondary">{i + 1}</td>
              <td className="px-3 py-2">
                <p className="font-medium text-fg">{r.cn}</p>
                <p className="font-mono text-[10px] text-fg-secondary opacity-60">{truncate(r.dn, 60)}</p>
              </td>
              <td className="px-3 py-2 text-right font-medium text-fg">{fmtDuration(r.totalTimeMs)}</td>
              <td className="px-3 py-2 text-right text-fg-secondary">{r.articleCount}</td>
              <td className="px-3 py-2 text-right text-fg-secondary">{r.pingCount}</td>
              <td className="px-3 py-2 text-right text-fg-secondary">{r.maxScrollDepth}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Nav method detail table ───────────────────────────────────────────────────

function NavMethodTable({ entries }: { entries: ArticleNavMethodEntry[] }) {
  const sorted = [...entries].sort((a, b) => b.uniqueClientsCount - a.uniqueClientsCount)
  return (
    <div className="overflow-hidden rounded border border-line">
      <table className="w-full text-[11px]">
        <thead className="bg-fill">
          <tr>
            <th className="px-3 py-2 text-left font-semibold text-fg-secondary">Method</th>
            <th className="px-3 py-2 text-right font-semibold text-fg-secondary">Readers</th>
            <th className="px-3 py-2 text-right font-semibold text-fg-secondary">Orgs</th>
            <th className="px-3 py-2 text-left font-semibold text-fg-secondary">Top Page</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {sorted.map(e => {
            const topPage = [...e.pages].sort((a, b) => b.navPageCount - a.navPageCount)[0]
            return (
              <tr key={e.navMethod} className="hover:bg-fill/50">
                <td className="px-3 py-2 font-medium text-fg">{fmtNavMethod(e.navMethod)}</td>
                <td className="px-3 py-2 text-right text-fg">{e.uniqueClientsCount.toLocaleString()}</td>
                <td className="px-3 py-2 text-right text-fg-secondary">{e.assignedOrgs.length}</td>
                <td className="px-3 py-2 text-fg-secondary">
                  {topPage ? (topPage.pageTitle ?? topPage.pageId) : '—'}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ── Section header ────────────────────────────────────────────────────────────

function SectionHeader({ label }: { label: string }) {
  return (
    <p className="mb-3 text-[9px] font-bold uppercase tracking-widest text-fg-secondary">
      {label}
    </p>
  )
}

// ── Shared: date range controls ───────────────────────────────────────────────

function DateRangeControls({
  from, to, loading, lastFetch, disabled,
  onFromChange, onToChange, onRefresh,
}: {
  from: string; to: string; loading: boolean; lastFetch: string | null; disabled?: boolean
  onFromChange: (v: string) => void; onToChange: (v: string) => void; onRefresh: () => void
}) {
  const inputCls = 'rounded border border-line bg-canvas px-2 py-1 text-xs text-fg focus:border-accent-primary-solid focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed'
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex items-center gap-2">
        <label className="text-[10px] font-bold uppercase tracking-widest text-fg-secondary">From</label>
        <input type="date" value={from} max={to} disabled={disabled}
          onChange={e => onFromChange(e.target.value)} className={inputCls} />
      </div>
      <div className="flex items-center gap-2">
        <label className="text-[10px] font-bold uppercase tracking-widest text-fg-secondary">To</label>
        <input type="date" value={to} min={from} max={todayStr()} disabled={disabled}
          onChange={e => onToChange(e.target.value)} className={inputCls} />
      </div>
      <button type="button" onClick={onRefresh} disabled={loading || disabled}
        className="flex items-center gap-1.5 rounded border border-line bg-canvas px-3 py-1.5 text-[11px] font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg disabled:opacity-50">
        {loading ? <Loader2 size={11} className="animate-spin" aria-hidden="true" /> : <RefreshCw size={11} aria-hidden="true" />}
        {loading ? 'Loading…' : 'Refresh'}
      </button>
      {lastFetch && !loading && (
        <p className="text-[10px] text-fg-secondary opacity-60">Updated {lastFetch}</p>
      )}
    </div>
  )
}

// ── Shared: activity panel (stat cards + top articles + readers) ──────────────

function ActivityPanel({
  rows, titles, colors, emptyMsg = 'No readership data for this selection.', topArticlesChartId,
}: {
  rows: ArticleActivityEntry[]
  titles: Map<string, string>
  colors: ChartColors
  emptyMsg?: string
  topArticlesChartId?: string
}) {
  const rollups = useMemo(() => rollupActivity(rows), [rows])
  const clientRollups = useMemo(() => buildClientRollups(rows), [rows])
  const totalReads = rows.length
  const totalTimeMs = rows.reduce((s, r) => s + r.timeSpentMs, 0)
  const avgTimeMs = totalReads > 0 ? Math.round(totalTimeMs / totalReads) : 0
  const avgScrollDepth = totalReads > 0 ? Math.round(rows.reduce((s, r) => s + r.maxScrollYIndex, 0) / totalReads) : 0

  if (totalReads === 0) {
    return <p className="py-12 text-center text-sm text-fg-secondary opacity-60">{emptyMsg}</p>
  }
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <StatCard icon={BookOpen}  label="Article Reads"    value={totalReads.toLocaleString()} />
        <StatCard icon={Users}     label="Unique Readers"   value={clientRollups.length.toLocaleString()} />
        <StatCard icon={Clock}     label="Avg Time Spent"   value={fmtDuration(avgTimeMs)} sub="per read" />
        <StatCard icon={Clock}     label="Total Time"       value={fmtDuration(totalTimeMs)} />
        <StatCard icon={ArrowDown} label="Avg Scroll Depth" value={String(avgScrollDepth)} sub="block index" />
      </div>
      {rollups.length > 1 && (
        <div>
          <SectionHeader label={`Top Articles by Time Spent (${Math.min(rollups.length, 15)} of ${rollups.length})`} />
          <div className="rounded-lg border border-line bg-surface p-4">
            <TopArticlesChart rollups={rollups} colors={colors} titles={titles} chartId={topArticlesChartId} />
          </div>
        </div>
      )}
      <div>
        <SectionHeader label={`Most Engaged Readers (top ${Math.min(clientRollups.length, 20)})`} />
        <ClientTable rows={clientRollups} />
      </div>
    </div>
  )
}

// ── Scroll depth panel ────────────────────────────────────────────────────────

function ScrollDepthPanel({ rows, colors, chartId }: { rows: ArticleActivityEntry[]; colors: ChartColors; chartId?: string }) {
  if (rows.length === 0) {
    return <p className="py-12 text-center text-sm text-fg-secondary opacity-60">No readership data for this article in the selected date range.</p>
  }

  const maxDepth   = Math.max(...rows.map(r => r.maxScrollYIndex), 0)
  const avgDepth   = Math.round(rows.reduce((s, r) => s + r.maxScrollYIndex, 0) / rows.length)
  const deepCount  = rows.filter(r => r.maxScrollYIndex >= Math.round(maxDepth * 0.75)).length
  const deepPct    = Math.round((deepCount / rows.length) * 100)

  // [blockIndex, pct of readers who reached AT LEAST that block]
  const curveData: [number, number][] = Array.from({ length: maxDepth + 1 }, (_, i) => [
    i,
    Math.round((rows.filter(r => r.maxScrollYIndex >= i).length / rows.length) * 100),
  ])

  const option = {
    backgroundColor: 'transparent',
    textStyle: { color: colors.fg },
    tooltip: {
      trigger: 'axis' as const,
      backgroundColor: colors.tooltipBg,
      borderColor: colors.line,
      textStyle: { color: colors.tooltipFg, fontSize: 11 },
      formatter: (params: { data: [number, number] }[]) => {
        const [block, pct] = params[0].data
        return `Block <b>${block}</b>: <b>${pct}%</b> of readers reached here`
      },
    },
    grid: { top: 16, right: 16, bottom: 40, left: 8, containLabel: true },
    xAxis: {
      type: 'value' as const,
      name: 'Block Index',
      nameLocation: 'middle' as const,
      nameGap: 28,
      nameTextStyle: { color: colors.fgSecondary, fontSize: 10 },
      min: 0,
      max: maxDepth,
      axisLabel: { color: colors.fgSecondary, fontSize: 10 },
      splitLine: { lineStyle: { color: colors.line } },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value' as const,
      min: 0,
      max: 100,
      axisLabel: {
        color: colors.fgSecondary,
        fontSize: 10,
        formatter: (v: number) => `${v}%`,
      },
      splitLine: { lineStyle: { color: colors.line } },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    series: [{
      type: 'line' as const,
      data: curveData,
      smooth: true,
      symbol: 'none',
      lineStyle: { color: colors.accent, width: 2 },
      areaStyle: { color: colors.accent, opacity: 0.12 },
    }],
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-3">
        <StatCard icon={ArrowDown} label="Avg Scroll Depth"  value={`Block ${avgDepth}`} sub={`of ${maxDepth} max observed`} />
        <StatCard icon={ArrowDown} label="Deepest Observed"  value={`Block ${maxDepth}`} />
        <StatCard icon={Users}     label="Deep Reads"        value={`${deepPct}%`} sub="reached 75% depth" />
      </div>
      <div>
        <SectionHeader label="Scroll Completion Curve" />
        <div className="rounded-lg border border-line bg-surface p-4" {...(chartId ? { 'data-chart': chartId } : {})}>
          <ReactECharts option={option} style={{ height: 260 }} />
        </div>
        <p className="mt-2 text-[10px] text-fg-secondary opacity-60">
          Each point shows the % of readers who scrolled to at least that block. 100% at block 0 = everyone opened the article.
        </p>
      </div>
    </div>
  )
}

// ── Discovery panel ───────────────────────────────────────────────────────────

function DiscoveryPanel({
  data,
  colors,
  pageNames,
  navPieChartId,
  pagesBarChartId,
}: {
  data: ArticleNavMethodsByArticleEntry
  colors: ChartColors
  pageNames: Map<string, string>
  navPieChartId?: string
  pagesBarChartId?: string
}) {
  if (data.totalViews === 0) {
    return <p className="py-12 text-center text-sm text-fg-secondary opacity-60">No discovery data for this article in the selected date range.</p>
  }

  const sortedMethods = [...data.navMethods].sort((a, b) => b.views - a.views)
  const sortedPages   = [...data.referringPages].sort((a, b) => b.views - a.views)
  const sortedOrgs    = [...data.orgBreakdown].sort((a, b) => b.views - a.views)

  const pageLabel = (id: string) => truncate(pageNames.get(id) ?? 'Unknown Page', 36)

  const navPieOption = sortedMethods.length === 0 ? null : {
    backgroundColor: 'transparent',
    textStyle: { color: colors.fg },
    tooltip: {
      trigger: 'item' as const,
      backgroundColor: colors.tooltipBg,
      borderColor: colors.line,
      textStyle: { color: colors.tooltipFg, fontSize: 11 },
      formatter: (p: { name: string; value: number; percent: number }) =>
        `${p.name}: <b>${p.value.toLocaleString()}</b> (${p.percent}%)`,
    },
    legend: {
      orient: 'vertical' as const,
      right: 0,
      top: 'middle',
      itemWidth: 10,
      itemHeight: 10,
      textStyle: { color: colors.fgSecondary, fontSize: 10 },
    },
    series: [{
      type: 'pie' as const,
      radius: ['40%', '70%'],
      center: ['35%', '50%'],
      data: sortedMethods.map((m, i) => ({
        name: fmtNavMethod(m.method),
        value: m.views,
        itemStyle: { color: NAV_METHOD_COLORS[i % NAV_METHOD_COLORS.length] },
      })),
      label: { show: false },
      emphasis: { label: { show: false } },
    }],
  }

  const pagesBarOption = sortedPages.length === 0 ? null : {
    backgroundColor: 'transparent',
    textStyle: { color: colors.fg },
    tooltip: {
      trigger: 'axis' as const,
      axisPointer: { type: 'shadow' as const },
      backgroundColor: colors.tooltipBg,
      borderColor: colors.line,
      textStyle: { color: colors.tooltipFg, fontSize: 11 },
      formatter: (params: { dataIndex: number }[]) => {
        const p = sortedPages[params[0].dataIndex]
        const title = pageNames.get(p.pageId) ?? p.pageId
        return `<b>${truncate(title, 60)}</b><br/>Views: <b>${p.views.toLocaleString()}</b>`
      },
    },
    grid: { top: 8, right: 60, bottom: 8, left: 8, containLabel: true },
    xAxis: {
      type: 'value' as const,
      axisLabel: { color: colors.fgSecondary, fontSize: 10 },
      splitLine: { lineStyle: { color: colors.line } },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'category' as const,
      data: sortedPages.map(p => pageLabel(p.pageId)),
      inverse: true,
      axisLabel: { color: colors.fgSecondary, fontSize: 10 },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    series: [{
      type: 'bar' as const,
      data: sortedPages.map(p => p.views),
      itemStyle: { color: colors.accent, borderRadius: [0, 3, 3, 0] },
      label: {
        show: true,
        position: 'right' as const,
        formatter: (p: { value: number }) => p.value.toLocaleString(),
        color: colors.fgSecondary,
        fontSize: 10,
      },
    }],
  }

  return (
    <div className="space-y-6">
      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <StatCard icon={BookOpen} label="Total Views"    value={data.totalViews.toLocaleString()} />
        <StatCard icon={Users}    label="Unique Readers" value={data.uniqueClients.toLocaleString()} />
      </div>

      {/* Navigation methods */}
      {sortedMethods.length > 0 && (
        <div className="grid gap-6 lg:grid-cols-2">
          <div>
            <SectionHeader label="How Readers Arrived" />
            <div className="rounded-lg border border-line bg-surface p-4">
              {navPieOption && (
              <div {...(navPieChartId ? { 'data-chart': navPieChartId } : {})}>
                <ReactECharts option={navPieOption} style={{ height: 220 }} />
              </div>
            )}
            </div>
          </div>
          <div>
            <SectionHeader label="Navigation Breakdown" />
            <div className="overflow-hidden rounded border border-line">
              <table className="w-full text-[11px]">
                <thead className="bg-fill">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-fg-secondary">Method</th>
                    <th className="px-3 py-2 text-right font-semibold text-fg-secondary">Views</th>
                    <th className="px-3 py-2 text-right font-semibold text-fg-secondary">Readers</th>
                    <th className="px-3 py-2 text-right font-semibold text-fg-secondary">Share</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {sortedMethods.map((m, i) => (
                    <tr key={m.method} className="hover:bg-fill/50">
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: NAV_METHOD_COLORS[i % NAV_METHOD_COLORS.length] }} />
                          <span className="font-medium text-fg">{fmtNavMethod(m.method)}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right text-fg">{m.views.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right text-fg-secondary">{m.uniqueClients}</td>
                      <td className="px-3 py-2 text-right text-fg-secondary">{Math.round((m.views / data.totalViews) * 100)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Referring pages */}
      {sortedPages.length > 0 && pagesBarOption && (
        <div>
          <SectionHeader label={`Referring Pages (${sortedPages.length})`} />
          <div className="rounded-lg border border-line bg-surface p-4" {...(pagesBarChartId ? { 'data-chart': pagesBarChartId } : {})}>
            <ReactECharts option={pagesBarOption} style={{ height: Math.max(160, sortedPages.length * 28) }} />
          </div>
          <p className="mt-2 text-[10px] text-fg-secondary opacity-60">
            Pages readers were viewing when they navigated to this article.
          </p>
        </div>
      )}

      {/* Org breakdown */}
      {sortedOrgs.length > 0 && (
        <div>
          <SectionHeader label="Organization Breakdown" />
          <div className="overflow-hidden rounded border border-line">
            <table className="w-full text-[11px]">
              <thead className="bg-fill">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-fg-secondary">Organization</th>
                  <th className="px-3 py-2 text-right font-semibold text-fg-secondary">Views</th>
                  <th className="px-3 py-2 text-right font-semibold text-fg-secondary">Share</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {sortedOrgs.map(o => (
                  <tr key={o.org} className="hover:bg-fill/50">
                    <td className="px-3 py-2 font-medium text-fg">{o.org}</td>
                    <td className="px-3 py-2 text-right text-fg">{o.views.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right text-fg-secondary">{Math.round((o.views / data.totalViews) * 100)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

// ── By Article view ───────────────────────────────────────────────────────────

type ByArticleLens = 'engagement' | 'depth' | 'discovery' | 'visuals'

const ARTICLE_LENSES: { id: ByArticleLens; label: string; icon: React.ElementType }[] = [
  { id: 'engagement', label: 'Engagement',   icon: Clock      },
  { id: 'depth',      label: 'Scroll Depth', icon: ArrowDown  },
  { id: 'discovery',  label: 'Discovery',    icon: Navigation },
  { id: 'visuals',    label: 'Visuals',      icon: Image      },
]

function ByArticleView({ colors }: { colors: ChartColors }) {
  const [lens, setLens] = useState<ByArticleLens>('engagement')

  const [query, setQuery]               = useState('')
  const [searchResults, setSearchResults] = useState<WireArticle[]>([])
  const [searching, setSearching]       = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const [selected, setSelected]         = useState<WireArticle | null>(null)

  const [from, setFrom]       = useState(nDaysAgoStr(30))
  const [to, setTo]           = useState(todayStr())
  const [rows, setRows]       = useState<ArticleActivityEntry[]>([])
  const [titles, setTitles]   = useState<Map<string, string>>(new Map())
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState<string | null>(null)
  const [lastFetch, setLastFetch] = useState<string | null>(null)

  const [discoveryData, setDiscoveryData]       = useState<ArticleNavMethodsByArticleEntry | null>(null)
  const [discoveryLoading, setDiscoveryLoading] = useState(false)
  const [discoveryError, setDiscoveryError]     = useState<string | null>(null)
  const [pageNames, setPageNames]               = useState<Map<string, string>>(new Map())

  const [visualRows, setVisualRows]         = useState<ArticleVisualActivityEntry[]>([])
  const [visualLoading, setVisualLoading]   = useState(false)
  const [visualError, setVisualError]       = useState<string | null>(null)

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const searchRef   = useRef<HTMLDivElement>(null)

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (query.trim().length < 2) { setSearchResults([]); setShowDropdown(false); return }
    debounceRef.current = setTimeout(async () => {
      setSearching(true)
      try {
        const results = await httpArticleService.search(query.trim(), { })
        setSearchResults(results.slice(0, 12))
        setShowDropdown(results.length > 0)
      } catch { setSearchResults([]) }
      finally { setSearching(false) }
    }, 300)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [query])

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setShowDropdown(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleSelect = (article: WireArticle) => {
    setSelected(article)
    setQuery('')
    setShowDropdown(false)
    setSearchResults([])
    setTitles(new Map([[article.id, article.title]]))
    setDiscoveryData(null)
    setVisualRows([])
  }

  const fetchMetrics = useCallback(async () => {
    if (!selected) return
    setLoading(true)
    setError(null)
    try {
      const params = { from: dateToApi(from, false), to: dateToApi(to, true) }
      const { data } = await metricsApiClient.get<ArticleActivityEntry[]>(
        `/metrics/article/activity/per-article/${selected.id}`, { params }
      )
      setRows(data ?? [])
      setLastFetch(new Date().toLocaleTimeString())
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [selected, from, to])

  useEffect(() => {
    if (selected) void fetchMetrics()
  }, [fetchMetrics, selected])

  const fetchDiscovery = useCallback(async () => {
    if (!selected) return
    setDiscoveryLoading(true)
    setDiscoveryError(null)
    try {
      const params = { from: dateToApi(from, false), to: dateToApi(to, true) }
      const { data } = await metricsApiClient.get<ArticleNavMethodsByArticleEntry>(
        `/metrics/nav-methods/${selected.id}`, { params }
      )
      setDiscoveryData(data ?? null)
      if (data && data.referringPages.length > 0) {
        Promise.all([
          httpPageService.list({ status: 'live' }),
          httpPageService.list({ status: 'archived' }),
        ])
          .then(([live, archived]) => {
            const map = new Map<string, string>()
            for (const p of [...live, ...archived]) map.set(p.id, p.title)
            setPageNames(map)
          })
          .catch(() => {})
      }
      setLastFetch(new Date().toLocaleTimeString())
    } catch (e: unknown) {
      setDiscoveryError(e instanceof Error ? e.message : String(e))
    } finally {
      setDiscoveryLoading(false)
    }
  }, [selected, from, to])

  useEffect(() => {
    if (selected && lens === 'discovery') void fetchDiscovery()
  }, [fetchDiscovery, selected, lens])

  const fetchVisuals = useCallback(async () => {
    if (!selected) return
    setVisualLoading(true)
    setVisualError(null)
    try {
      const params = { from: dateToApi(from, false), to: dateToApi(to, true) }
      const { data } = await metricsApiClient.get<ArticleVisualActivityEntry[]>(
        `/metrics/article/visuals/activity/per-article/${selected.id}`,
        { params },
      )
      setVisualRows(data ?? [])
      setLastFetch(new Date().toLocaleTimeString())
    } catch (e: unknown) {
      setVisualError(e instanceof Error ? e.message : String(e))
    } finally {
      setVisualLoading(false)
    }
  }, [selected, from, to])

  useEffect(() => {
    if (selected && lens === 'visuals') void fetchVisuals()
  }, [fetchVisuals, selected, lens])

  const { downloadPdf, isGenerating } = useReadershipPdf()

  async function handleExport() {
    if (!selected) return
    const slug = (selected.documentId ?? selected.title).toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40)
    let data: ReadershipsReportData

    if (lens === 'engagement') {
      if (rows.length === 0) return
      const clientRollups  = buildClientRollups(rows)
      const totalTimeMs    = rows.reduce((s, r) => s + r.timeSpentMs, 0)
      const avgTimeMs      = rows.length > 0 ? Math.round(totalTimeMs / rows.length) : 0
      const avgScrollDepth = rows.length > 0 ? Math.round(rows.reduce((s, r) => s + r.maxScrollYIndex, 0) / rows.length) : 0
      data = {
        viewLabel: 'By Article',
        subtitle:  selected.title,
        dateRange: `${from} – ${to}`,
        stats: [
          { label: 'Article Reads',    value: rows.length.toLocaleString() },
          { label: 'Unique Readers',   value: clientRollups.length.toLocaleString() },
          { label: 'Avg Time Spent',   value: fmtDuration(avgTimeMs), sub: 'per read' },
          { label: 'Total Time',       value: fmtDuration(totalTimeMs) },
          { label: 'Avg Scroll Depth', value: String(avgScrollDepth), sub: 'block index' },
        ],
        sections: [
          {
            label: 'Reader Breakdown',
            chartImageUrl: captureChart('article-top-articles') ?? undefined,
            columns: [
              { key: 'reader',    label: 'Reader',     flex: 2 },
              { key: 'timeSpent', label: 'Time Spent', flex: 1, align: 'right' },
              { key: 'pings',     label: 'Pings',      flex: 1, align: 'right' },
              { key: 'maxScroll', label: 'Max Scroll', flex: 1, align: 'right' },
            ],
            rows: clientRollups.slice(0, 50).map(r => ({
              reader:    r.cn,
              timeSpent: fmtDuration(r.totalTimeMs),
              pings:     r.pingCount,
              maxScroll: r.maxScrollDepth,
            })),
          },
        ],
      }
    } else if (lens === 'depth') {
      if (rows.length === 0) return
      const maxDepth   = Math.max(...rows.map(r => r.maxScrollYIndex), 0)
      const avgDepth   = Math.round(rows.reduce((s, r) => s + r.maxScrollYIndex, 0) / rows.length)
      const deepCount  = rows.filter(r => r.maxScrollYIndex >= Math.round(maxDepth * 0.75)).length
      const deepPct    = Math.round((deepCount / rows.length) * 100)
      data = {
        viewLabel: 'By Article',
        subtitle:  selected.title,
        dateRange: `${from} – ${to}`,
        stats: [
          { label: 'Avg Scroll Depth',  value: `Block ${avgDepth}`, sub: `of ${maxDepth} max observed` },
          { label: 'Deepest Observed',  value: `Block ${maxDepth}` },
          { label: 'Deep Reads',        value: `${deepPct}%`, sub: 'reached 75% depth' },
        ],
        sections: [
          {
            label: 'Scroll Completion Curve',
            chartImageUrl: captureChart('article-scroll-depth') ?? undefined,
            chartHeight: 180,
            columns: [],
            rows: [],
          },
        ],
      }
    } else {
      // discovery lens
      if (!discoveryData) return
      data = {
        viewLabel: 'By Article',
        subtitle:  selected.title,
        dateRange: `${from} – ${to}`,
        stats: [
          { label: 'Total Views',    value: discoveryData.totalViews.toLocaleString() },
          { label: 'Unique Readers', value: String(discoveryData.uniqueClients) },
        ],
        sections: [
          {
            label: 'Navigation Methods',
            chartImageUrl: captureChart('article-nav-pie') ?? undefined,
            columns: [
              { key: 'method',  label: 'Method',         flex: 2 },
              { key: 'views',   label: 'Views',           flex: 1, align: 'right' },
              { key: 'readers', label: 'Unique Readers',  flex: 1, align: 'right' },
            ],
            rows: [...discoveryData.navMethods]
              .sort((a, b) => b.views - a.views)
              .map(m => ({
                method:  fmtNavMethod(m.method),
                views:   m.views,
                readers: m.uniqueClients,
              })),
          },
          {
            label: 'Referring Pages',
            chartImageUrl: captureChart('article-pages-bar') ?? undefined,
            columns: [
              { key: 'page',  label: 'Page',  flex: 3 },
              { key: 'views', label: 'Views', flex: 1, align: 'right' },
            ],
            rows: [...discoveryData.referringPages]
              .sort((a, b) => b.views - a.views)
              .map(p => ({
                page:  pageNames.get(p.pageId) ?? p.pageId,
                views: p.views,
              })),
          },
          {
            label: 'Org Breakdown',
            columns: [
              { key: 'org',   label: 'Organization', flex: 3 },
              { key: 'views', label: 'Views',         flex: 1, align: 'right' },
            ],
            rows: [...discoveryData.orgBreakdown]
              .sort((a, b) => b.views - a.views)
              .map(o => ({ org: o.org, views: o.views })),
          },
        ],
      }
    }
    await downloadPdf(data, `readership-by-article-${slug}-${from}-to-${to}`)
  }

  return (
    <div className="space-y-6">
      {/* Search */}
      <div ref={searchRef} className="relative max-w-lg">
        <div className="flex items-center gap-2 rounded border border-line bg-canvas px-3 py-2">
          {searching
            ? <Loader2 size={13} className="shrink-0 animate-spin text-fg-secondary" />
            : <Search size={13} className="shrink-0 text-fg-secondary" />}
          <input
            type="text"
            placeholder="Search for an article by title…"
            value={query}
            onChange={e => { setQuery(e.target.value); setSelected(null) }}
            onFocus={() => { if (searchResults.length > 0) setShowDropdown(true) }}
            className="min-w-0 flex-1 bg-transparent text-sm text-fg placeholder:text-fg-secondary/50 focus:outline-none"
          />
        </div>
        {showDropdown && (
          <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-72 overflow-y-auto rounded border border-line bg-surface shadow-lg">
            {searchResults.map(a => (
              <button key={a.id} type="button"
                className="flex w-full flex-col px-3 py-2 text-left hover:bg-fill"
                onMouseDown={() => handleSelect(a)}>
                <p className="truncate text-sm font-medium text-fg">{a.title}</p>
                {a.documentId && (
                  <p className="font-mono text-[10px] text-fg-secondary opacity-70">{a.documentId}</p>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Selected article badge */}
      {selected && (
        <div className="flex items-start justify-between rounded-lg border border-accent-primary-line bg-accent-primary-surface px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-fg">{selected.title}</p>
            {selected.documentId && (
              <p className="font-mono text-[11px] text-fg-secondary">{selected.documentId}</p>
            )}
          </div>
          <button type="button" onClick={() => { setSelected(null); setRows([]); setDiscoveryData(null); setVisualRows([]) }}
            className="ml-3 shrink-0 rounded p-1 text-fg-secondary hover:bg-fill hover:text-fg">
            <X size={12} aria-label="Clear selection" />
          </button>
        </div>
      )}

      {selected && (
        <>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <DateRangeControls from={from} to={to}
              loading={lens === 'discovery' ? discoveryLoading : lens === 'visuals' ? visualLoading : loading}
              lastFetch={lastFetch}
              onFromChange={setFrom} onToChange={setTo}
              onRefresh={() => {
                if (lens === 'discovery') {
                  void fetchDiscovery()
                } else if (lens === 'visuals') {
                  void fetchMetrics()
                  void fetchVisuals()
                } else {
                  void fetchMetrics()
                }
              }} />
            {(
              (lens === 'engagement' && rows.length > 0 && !loading) ||
              (lens === 'depth'      && rows.length > 0 && !loading) ||
              (lens === 'discovery'  && discoveryData != null && !discoveryLoading)
            ) && (
              <button type="button" onClick={() => void handleExport()} disabled={isGenerating}
                className="flex items-center gap-1.5 rounded border border-line bg-canvas px-3 py-1.5 text-[11px] font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg disabled:opacity-50">
                {isGenerating
                  ? <Loader2 size={11} className="animate-spin" aria-hidden="true" />
                  : <FileDown size={11} aria-hidden="true" />}
                {isGenerating ? 'Generating…' : 'Export PDF'}
              </button>
            )}
          </div>

          {/* Lens selector */}
          <div className="flex gap-1 rounded-md border border-line bg-fill p-0.5 w-fit">
            {ARTICLE_LENSES.map(({ id, label, icon: Icon }) => (
              <button key={id} type="button"
                onClick={() => setLens(id)}
                className={[
                  'flex items-center gap-1.5 rounded px-3 py-1.5 text-[11px] font-semibold transition-colors',
                  lens === id
                    ? 'bg-canvas text-fg shadow-sm'
                    : 'text-fg-secondary hover:text-fg',
                ].join(' ')}>
                <Icon size={11} aria-hidden="true" />
                {label}
              </button>
            ))}
          </div>

          {error && (lens === 'engagement' || lens === 'depth') && (
            <div className="rounded border border-error-line bg-error-fill px-4 py-3 text-sm text-error-fg">{error}</div>
          )}
          {discoveryError && lens === 'discovery' && (
            <div className="rounded border border-error-line bg-error-fill px-4 py-3 text-sm text-error-fg">{discoveryError}</div>
          )}
          {visualError && lens === 'visuals' && (
            <div className="rounded border border-error-line bg-error-fill px-4 py-3 text-sm text-error-fg">{visualError}</div>
          )}
          {!loading && !error && lens === 'engagement' && (
            <ActivityPanel rows={rows} titles={titles} colors={colors}
              topArticlesChartId="article-top-articles"
              emptyMsg="No readership data found for this article in the selected date range." />
          )}
          {!loading && !error && lens === 'depth' && (
            <ScrollDepthPanel rows={rows} colors={colors} chartId="article-scroll-depth" />
          )}
          {!discoveryLoading && !discoveryError && lens === 'discovery' && discoveryData && (
            <DiscoveryPanel data={discoveryData} colors={colors} pageNames={pageNames}
              navPieChartId="article-nav-pie" pagesBarChartId="article-pages-bar" />
          )}
          {!discoveryLoading && !discoveryError && lens === 'discovery' && !discoveryData && (
            <p className="py-12 text-center text-sm text-fg-secondary opacity-60">No discovery data found for this article in the selected date range.</p>
          )}
          {!visualLoading && !visualError && lens === 'visuals' && (
            <VisualsPanel rows={visualRows} totalReaders={new Set(rows.map(r => r.clientDn)).size} />
          )}
        </>
      )}

      {!selected && !query && (
        <p className="py-12 text-center text-sm text-fg-secondary opacity-60">
          Search for an article above to view its readership metrics.
        </p>
      )}
    </div>
  )
}

// ── By Page view ──────────────────────────────────────────────────────────────

const PAGE_KIND_LABELS: Record<string, string> = {
  'front-page': 'Front Page',
  topic:        'Topic',
  region:       'Region',
  edition:      'Edition',
  book:         'Book',
}

function ByPageView({ colors }: { colors: ChartColors }) {
  const [pages, setPages]             = useState<Page[]>([])
  const [pagesLoading, setPagesLoading] = useState(false)
  const [includeArchived, setIncludeArchived] = useState(false)

  const [selectedPageId, setSelectedPageId] = useState<string>('')
  const [limitToLiveDates, setLimitToLiveDates] = useState(false)

  const [from, setFrom] = useState(nDaysAgoStr(30))
  const [to, setTo]     = useState(todayStr())

  const [rows, setRows]     = useState<ArticleActivityEntry[]>([])
  const [titles, setTitles] = useState<Map<string, string>>(new Map())
  const [loading, setLoading]   = useState(false)
  const [error, setError]       = useState<string | null>(null)
  const [lastFetch, setLastFetch] = useState<string | null>(null)

  const [poolArticleIds, setPoolArticleIds] = useState<string[]>([])
  const [poolLoading, setPoolLoading]       = useState(false)

  const selectedPage = useMemo(
    () => pages.find(p => p.id === selectedPageId) ?? null,
    [pages, selectedPageId],
  )
  const pageArticleIds = useMemo(
    () => selectedPage ? extractArticleIdsFromPage(selectedPage) : [],
    [selectedPage],
  )
  const isPoolPage = useMemo(
    () => !!(selectedPage?.pool?.topicSlug || selectedPage?.pool?.regionSlug),
    [selectedPage],
  )
  const effectiveArticleIds = useMemo(
    () => pageArticleIds.length > 0 ? pageArticleIds : poolArticleIds,
    [pageArticleIds, poolArticleIds],
  )

  // Load pages
  useEffect(() => {
    setPagesLoading(true)
    const fetches = [httpPageService.list({ status: 'live' })]
    if (includeArchived) fetches.push(httpPageService.list({ status: 'archived' }))
    Promise.all(fetches)
      .then(results => setPages(results.flat().sort((a, b) => a.title.localeCompare(b.title))))
      .catch(() => setPages([]))
      .finally(() => setPagesLoading(false))
  }, [includeArchived])

  // Auto-fill dates when "limit to page live dates" is toggled
  useEffect(() => {
    if (limitToLiveDates && selectedPage) {
      if (selectedPage.publishedAt) setFrom(isoToDateStr(selectedPage.publishedAt))
      setTo(selectedPage.archivedAt ? isoToDateStr(selectedPage.archivedAt) : todayStr())
    }
  }, [limitToLiveDates, selectedPage])

  // Resolve current articles for pool-based (topic/region) pages
  useEffect(() => {
    if (!selectedPage || !isPoolPage || pageArticleIds.length > 0) {
      setPoolArticleIds([])
      return
    }
    setPoolLoading(true)
    httpArticleService.search('', {
      ...(selectedPage.pool?.topicSlug  ? { issuesAndTopics:      [selectedPage.pool.topicSlug]  } : {}),
      ...(selectedPage.pool?.regionSlug ? { regionsAndCountries:  [selectedPage.pool.regionSlug] } : {}),
    })
      .then(articles => setPoolArticleIds(articles.map(a => a.id)))
      .catch(() => setPoolArticleIds([]))
      .finally(() => setPoolLoading(false))
  }, [selectedPage, isPoolPage, pageArticleIds.length])

  const fetchMetrics = useCallback(async () => {
    if (!selectedPage || effectiveArticleIds.length === 0) return
    setLoading(true)
    setError(null)
    try {
      const params = { from: dateToApi(from, false), to: dateToApi(to, true) }
      const { data } = await metricsApiClient.get<ArticleActivityEntry[]>(
        '/metrics/article/activity', { params }
      )
      const filtered = (data ?? []).filter(r => effectiveArticleIds.includes(r.articleId))
      setRows(filtered)
      setLastFetch(new Date().toLocaleTimeString())

      const uniqueIds = Array.from(new Set(filtered.map(r => r.articleId))).filter(Boolean)
      if (uniqueIds.length > 0) {
        httpArticleService.getByIds(uniqueIds)
          .then(articles => {
            const map = new Map<string, string>()
            for (const a of articles) map.set(a.id, a.title ?? a.id)
            setTitles(map)
          })
          .catch(() => {})
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [selectedPage, effectiveArticleIds, from, to])

  useEffect(() => {
    if (selectedPage && effectiveArticleIds.length > 0) void fetchMetrics()
  }, [fetchMetrics, selectedPage, effectiveArticleIds])

  const grouped = useMemo(() => {
    const map = new Map<string, Page[]>()
    for (const p of pages) {
      const k = p.kind
      if (!map.has(k)) map.set(k, [])
      map.get(k)!.push(p)
    }
    return map
  }, [pages])

  const { downloadPdf, isGenerating } = useReadershipPdf()

  async function handleExport() {
    if (!selectedPage || rows.length === 0) return
    const rollups      = rollupActivity(rows)
    const clientRollups = buildClientRollups(rows)
    const totalReads   = rows.length
    const totalTimeMs  = rows.reduce((s, r) => s + r.timeSpentMs, 0)
    const avgTimeMs    = totalReads > 0 ? Math.round(totalTimeMs / totalReads) : 0
    const avgScrollDepth = totalReads > 0
      ? Math.round(rows.reduce((s, r) => s + r.maxScrollYIndex, 0) / totalReads)
      : 0
    const data: ReadershipsReportData = {
      viewLabel: 'By Page',
      subtitle:  selectedPage.title,
      dateRange: `${from} – ${to}`,
      stats: [
        { label: 'Article Reads',    value: totalReads.toLocaleString() },
        { label: 'Unique Readers',   value: clientRollups.length.toLocaleString() },
        { label: 'Avg Time Spent',   value: fmtDuration(avgTimeMs), sub: 'per read' },
        { label: 'Total Time',       value: fmtDuration(totalTimeMs) },
        { label: 'Avg Scroll Depth', value: String(avgScrollDepth), sub: 'block index' },
      ],
      sections: [
        {
          label: `Top Articles by Time Spent (${rollups.length})`,
          chartImageUrl: captureChart('page-top-articles') ?? undefined,
          columns: [
            { key: 'title',     label: 'Article',    flex: 3 },
            { key: 'timeSpent', label: 'Time Spent', flex: 1, align: 'right' },
            { key: 'readers',   label: 'Readers',    flex: 1, align: 'right' },
            { key: 'avgScroll', label: 'Avg Scroll', flex: 1, align: 'right' },
            { key: 'pings',     label: 'Pings',      flex: 1, align: 'right' },
          ],
          rows: rollups.slice(0, 50).map(r => ({
            title:     titles.get(r.id) ?? r.id,
            timeSpent: fmtDuration(r.totalTimeMs),
            readers:   r.uniqueReaders,
            avgScroll: r.avgScrollDepth,
            pings:     r.pingCount,
          })),
        },
        {
          label: `Most Engaged Readers (${Math.min(clientRollups.length, 25)})`,
          columns: [
            { key: 'reader',    label: 'Reader',     flex: 2 },
            { key: 'timeSpent', label: 'Time Spent', flex: 1, align: 'right' },
            { key: 'articles',  label: 'Articles',   flex: 1, align: 'right' },
            { key: 'pings',     label: 'Pings',      flex: 1, align: 'right' },
            { key: 'maxScroll', label: 'Max Scroll', flex: 1, align: 'right' },
          ],
          rows: clientRollups.slice(0, 25).map(r => ({
            reader:    r.cn,
            timeSpent: fmtDuration(r.totalTimeMs),
            articles:  r.articleCount,
            pings:     r.pingCount,
            maxScroll: r.maxScrollDepth,
          })),
        },
      ],
    }
    const slug = selectedPage.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40)
    await downloadPdf(data, `readership-by-page-${slug}-${from}-to-${to}`)
  }

  return (
    <div className="space-y-6">
      {/* Page picker */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <label className="text-[10px] font-bold uppercase tracking-widest text-fg-secondary">Page</label>
          <div className="relative">
            {pagesLoading && <Loader2 size={11} className="absolute left-2 top-1/2 -translate-y-1/2 animate-spin text-fg-secondary" />}
            <select
              value={selectedPageId}
              disabled={pagesLoading}
              onChange={e => {
                setSelectedPageId(e.target.value)
                setRows([])
                setTitles(new Map())
                setPoolArticleIds([])
              }}
              className="rounded border border-line bg-canvas py-1 pl-3 pr-8 text-xs text-fg focus:border-accent-primary-solid focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="">— Select a page —</option>
              {Array.from(grouped.entries()).map(([kind, kindPages]) => (
                <optgroup key={kind} label={PAGE_KIND_LABELS[kind] ?? kind}>
                  {kindPages.map(p => (
                    <option key={p.id} value={p.id}>{p.title}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-[11px] text-fg-secondary">
          <input type="checkbox" checked={includeArchived} onChange={e => setIncludeArchived(e.target.checked)}
            className="rounded" />
          Include archived pages
        </label>
      </div>

      {selectedPage && (
        <>
          {/* Page info + live-date option */}
          <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-line bg-surface px-4 py-3">
            <div>
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-fg">{selectedPage.title}</p>
                <span className="rounded-full border border-line px-2 py-0.5 text-[10px] font-medium text-fg-secondary">
                  {PAGE_KIND_LABELS[selectedPage.kind] ?? selectedPage.kind}
                </span>
                <span className="rounded-full border border-line px-2 py-0.5 text-[10px] font-medium text-fg-secondary capitalize">
                  {selectedPage.status}
                </span>
              </div>
              <p className="mt-0.5 text-[11px] text-fg-secondary">
                {pageArticleIds.length > 0
                  ? `${pageArticleIds.length} static article slot${pageArticleIds.length !== 1 ? 's' : ''}`
                  : isPoolPage
                    ? poolLoading
                      ? 'Resolving pool articles…'
                      : `${poolArticleIds.length} article${poolArticleIds.length !== 1 ? 's' : ''} in current pool`
                    : 'No articles configured'}
                {selectedPage.publishedAt && (
                  <> · Live since {isoToDateStr(selectedPage.publishedAt)}</>
                )}
                {selectedPage.archivedAt && (
                  <> · Archived {isoToDateStr(selectedPage.archivedAt)}</>
                )}
              </p>
            </div>
            {selectedPage.publishedAt && (
              <label className="flex cursor-pointer items-center gap-2 text-[11px] text-fg-secondary">
                <input type="checkbox" checked={limitToLiveDates} onChange={e => setLimitToLiveDates(e.target.checked)}
                  className="rounded" />
                Limit to page live dates
              </label>
            )}
          </div>

          {poolLoading ? (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-fg-secondary">
              <Loader2 size={14} className="animate-spin" aria-hidden="true" />
              Resolving current pool articles…
            </div>
          ) : effectiveArticleIds.length === 0 ? (
            <p className="py-6 text-center text-sm text-fg-secondary opacity-60">
              {isPoolPage
                ? 'No articles found in the current pool for this page.'
                : 'This page has no statically pinned articles — metrics are not available for fully dynamic pages.'}
            </p>
          ) : (
            <>
              {isPoolPage && (
                <p className="text-[10px] text-amber-600 dark:text-amber-400 opacity-80">
                  Showing metrics for the {poolArticleIds.length} article{poolArticleIds.length !== 1 ? 's' : ''} currently tagged to this {selectedPage.kind}.
                  The pool is dynamic — article counts may differ from the selected date window.
                </p>
              )}
              <div className="flex flex-wrap items-start justify-between gap-3">
                <DateRangeControls from={from} to={to}
                  loading={loading}
                  lastFetch={lastFetch}
                  disabled={limitToLiveDates}
                  onFromChange={setFrom}
                  onToChange={setTo}
                  onRefresh={() => void fetchMetrics()} />
                {rows.length > 0 && !loading && (
                  <button type="button" onClick={() => void handleExport()} disabled={isGenerating}
                    className="flex items-center gap-1.5 rounded border border-line bg-canvas px-3 py-1.5 text-[11px] font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg disabled:opacity-50">
                    {isGenerating
                      ? <Loader2 size={11} className="animate-spin" aria-hidden="true" />
                      : <FileDown size={11} aria-hidden="true" />}
                    {isGenerating ? 'Generating…' : 'Export PDF'}
                  </button>
                )}
              </div>
              {error && (
                <div className="rounded border border-error-line bg-error-fill px-4 py-3 text-sm text-error-fg">{error}</div>
              )}
              {!loading && !error && (
                <ActivityPanel rows={rows} titles={titles} colors={colors}
                  topArticlesChartId="page-top-articles"
                  emptyMsg="No readership data found for articles on this page in the selected date range." />
              )}
            </>
          )}
        </>
      )}

      {!selectedPage && (
        <p className="py-12 text-center text-sm text-fg-secondary opacity-60">
          Select a page above to view readership metrics for its articles.
        </p>
      )}
    </div>
  )
}

// ── By Date view (full aggregate) ─────────────────────────────────────────────

function ByDateView({ colors }: { colors: ChartColors }) {
  const [from, setFrom] = useState(nDaysAgoStr(30))
  const [to, setTo]     = useState(todayStr())

  const [activityRows, setActivityRows] = useState<ArticleActivityEntry[]>([])
  const [navEntries,   setNavEntries]   = useState<ArticleNavMethodEntry[]>([])
  const [articleTitles, setArticleTitles] = useState<Map<string, string>>(new Map())
  const [loading,  setLoading]  = useState(false)
  const [error,    setError]    = useState<string | null>(null)
  const [lastFetch, setLastFetch] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = { from: dateToApi(from, false), to: dateToApi(to, true) }
      const [actRes, navRes] = await Promise.all([
        metricsApiClient.get<ArticleActivityEntry[]>('/metrics/article/activity', { params }),
        metricsApiClient.get<ArticleNavMethodEntry[]>('/metrics/article/nav-methods', { params }),
      ])
      const rows = actRes.data ?? []
      setActivityRows(rows)
      setNavEntries(navRes.data ?? [])
      setLastFetch(new Date().toLocaleTimeString())
      const uniqueIds = Array.from(new Set(rows.map(r => r.articleId))).filter(Boolean)
      if (uniqueIds.length > 0) {
        httpArticleService.getByIds(uniqueIds)
          .then(articles => {
            const map = new Map<string, string>()
            for (const a of articles) map.set(a.id, a.title ?? a.id)
            setArticleTitles(map)
          })
          .catch(() => {})
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [from, to])

  useEffect(() => { void fetchData() }, [fetchData])

  const rollups      = useMemo(() => rollupActivity(activityRows), [activityRows])
  const clientRollups = useMemo(() => buildClientRollups(activityRows), [activityRows])
  const totalReads   = activityRows.length
  const totalTimeMs  = activityRows.reduce((s, r) => s + r.timeSpentMs, 0)
  const avgTimeMs    = totalReads > 0 ? Math.round(totalTimeMs / totalReads) : 0
  const topNavMethod = [...navEntries]
    .sort((a, b) => b.uniqueClientsCount - a.uniqueClientsCount)
    [0]?.navMethod ?? null

  const { downloadPdf, isGenerating } = useReadershipPdf()

  async function handleExport() {
    const avgScrollDepth = totalReads > 0
      ? Math.round(activityRows.reduce((s, r) => s + r.maxScrollYIndex, 0) / totalReads)
      : 0
    const data: ReadershipsReportData = {
      viewLabel: 'By Date',
      dateRange: `${from} – ${to}`,
      stats: [
        { label: 'Article Reads',    value: totalReads.toLocaleString() },
        { label: 'Unique Readers',   value: clientRollups.length.toLocaleString() },
        { label: 'Avg Time Spent',   value: fmtDuration(avgTimeMs), sub: 'per read' },
        { label: 'Top Nav Method',   value: topNavMethod ? fmtNavMethod(topNavMethod) : '—' },
        { label: 'Avg Scroll Depth', value: String(avgScrollDepth), sub: 'block index' },
      ],
      sections: [
        {
          label: `Top Articles by Time Spent (${rollups.length})`,
          chartImageUrl: captureChart('date-top-articles') ?? undefined,
          columns: [
            { key: 'title',     label: 'Article',    flex: 3 },
            { key: 'timeSpent', label: 'Time Spent', flex: 1, align: 'right' },
            { key: 'readers',   label: 'Readers',    flex: 1, align: 'right' },
            { key: 'avgScroll', label: 'Avg Scroll', flex: 1, align: 'right' },
            { key: 'pings',     label: 'Pings',      flex: 1, align: 'right' },
          ],
          rows: rollups.slice(0, 50).map(r => ({
            title:     articleTitles.get(r.id) ?? r.id,
            timeSpent: fmtDuration(r.totalTimeMs),
            readers:   r.uniqueReaders,
            avgScroll: r.avgScrollDepth,
            pings:     r.pingCount,
          })),
        },
        {
          label: 'Navigation Method Distribution',
          chartImageUrl: captureChart('date-nav-method') ?? undefined,
          columns: [
            { key: 'method',  label: 'Method',   flex: 2 },
            { key: 'readers', label: 'Readers',  flex: 1, align: 'right' },
            { key: 'orgs',    label: 'Orgs',     flex: 1, align: 'right' },
            { key: 'topPage', label: 'Top Page', flex: 2 },
          ],
          rows: [...navEntries]
            .sort((a, b) => b.uniqueClientsCount - a.uniqueClientsCount)
            .map(e => ({
              method:  fmtNavMethod(e.navMethod),
              readers: e.uniqueClientsCount,
              orgs:    e.assignedOrgs.length,
              topPage: [...e.pages].sort((a, b) => b.navPageCount - a.navPageCount)[0]?.pageTitle ?? '—',
            })),
        },
        {
          label: `Most Engaged Readers (${Math.min(clientRollups.length, 25)})`,
          columns: [
            { key: 'reader',    label: 'Reader',     flex: 2 },
            { key: 'timeSpent', label: 'Time Spent', flex: 1, align: 'right' },
            { key: 'articles',  label: 'Articles',   flex: 1, align: 'right' },
            { key: 'pings',     label: 'Pings',      flex: 1, align: 'right' },
            { key: 'maxScroll', label: 'Max Scroll', flex: 1, align: 'right' },
          ],
          rows: clientRollups.slice(0, 25).map(r => ({
            reader:    r.cn,
            timeSpent: fmtDuration(r.totalTimeMs),
            articles:  r.articleCount,
            pings:     r.pingCount,
            maxScroll: r.maxScrollDepth,
          })),
        },
      ],
    }
    await downloadPdf(data, `readership-by-date-${from}-to-${to}`)
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <DateRangeControls from={from} to={to} loading={loading} lastFetch={lastFetch}
          onFromChange={setFrom} onToChange={setTo} onRefresh={() => void fetchData()} />
        {totalReads > 0 && !loading && (
          <button type="button" onClick={() => void handleExport()} disabled={isGenerating}
            className="flex items-center gap-1.5 rounded border border-line bg-canvas px-3 py-1.5 text-[11px] font-semibold text-fg-secondary transition-colors hover:bg-fill hover:text-fg disabled:opacity-50">
            {isGenerating
              ? <Loader2 size={11} className="animate-spin" aria-hidden="true" />
              : <FileDown size={11} aria-hidden="true" />}
            {isGenerating ? 'Generating…' : 'Export PDF'}
          </button>
        )}
      </div>

      {error && (
        <div className="rounded border border-error-line bg-error-fill px-4 py-3 text-sm text-error-fg">{error}</div>
      )}

      {!error && !loading && totalReads === 0 && (
        <p className="py-12 text-center text-sm text-fg-secondary opacity-60">
          No readership data found for the selected date range.
        </p>
      )}

      {totalReads > 0 && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <StatCard icon={BookOpen}   label="Article Reads"   value={totalReads.toLocaleString()} />
            <StatCard icon={Users}      label="Unique Readers"  value={clientRollups.length.toLocaleString()} />
            <StatCard icon={Clock}      label="Avg Time Spent"  value={fmtDuration(avgTimeMs)} sub="per read" />
            <StatCard icon={Navigation} label="Top Nav Method"
              value={topNavMethod ? fmtNavMethod(topNavMethod) : '—'} />
            <StatCard icon={ArrowDown}  label="Avg Scroll Depth"
              value={String(activityRows.length > 0 ? Math.round(activityRows.reduce((s, r) => s + r.maxScrollYIndex, 0) / activityRows.length) : 0)}
              sub="block index" />
          </div>

          <div>
            <SectionHeader label={`Top Articles by Time Spent (${Math.min(rollups.length, 15)} of ${rollups.length})`} />
            <div className="rounded-lg border border-line bg-surface p-4">
              <TopArticlesChart rollups={rollups} colors={colors} titles={articleTitles} chartId="date-top-articles" />
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <div>
              <SectionHeader label="Navigation Method Distribution" />
              <div className="rounded-lg border border-line bg-surface p-4">
                <NavMethodChart entries={navEntries} colors={colors} chartId="date-nav-method" />
              </div>
            </div>
            <div>
              <SectionHeader label="Navigation Method Breakdown" />
              <NavMethodTable entries={navEntries} />
            </div>
          </div>

          <div>
            <SectionHeader label={`Most Engaged Readers (top ${Math.min(clientRollups.length, 20)})`} />
            <ClientTable rows={clientRollups} />
          </div>
        </>
      )}
    </div>
  )
}

// ── Sub-tab toggle ────────────────────────────────────────────────────────────

type ReadershipSubTab = 'article' | 'page' | 'date'

const SUB_TABS: { id: ReadershipSubTab; label: string; icon: React.ElementType }[] = [
  { id: 'article', label: 'By Article', icon: Search   },
  { id: 'page',    label: 'By Page',    icon: Layout   },
  { id: 'date',    label: 'By Date',    icon: Calendar },
]

// ── Main component ────────────────────────────────────────────────────────────

export function ReadershipDashboard() {
  const [subTab, setSubTab] = useState<ReadershipSubTab>('date')
  const colors = useChartTheme()

  return (
    <div className="space-y-6">
      {/* Sub-tab bar */}
      <div className="flex gap-1 rounded-lg border border-line bg-fill p-1 w-fit">
        {SUB_TABS.map(({ id, label, icon: Icon }) => (
          <button key={id} type="button" onClick={() => setSubTab(id)}
            className={[
              'flex items-center gap-1.5 rounded px-3 py-1.5 text-[11px] font-semibold transition-colors',
              subTab === id
                ? 'bg-canvas text-fg shadow-sm'
                : 'text-fg-secondary hover:text-fg',
            ].join(' ')}>
            <Icon size={11} aria-hidden="true" />
            {label}
          </button>
        ))}
      </div>

      {/* Views */}
      {subTab === 'article' && <ByArticleView colors={colors} />}
      {subTab === 'page'    && <ByPageView    colors={colors} />}
      {subTab === 'date'    && <ByDateView    colors={colors} />}
    </div>
  )
}
